import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Play,
  Edit3,
  Loader2,
  ClipboardEdit,
  Save,
  Globe,
  X,
  CheckCircle,
  XCircle,
  Terminal,
  Clock, 
  Image as ImageIcon,
  AlertTriangle,
} from "lucide-react";
import StepItem from "../../components/Common/StepItem";
import toast from "react-hot-toast";
import axios from "axios";
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";

export default function TestCaseDetails({ darkMode }) {
  const navigate = useNavigate();
  const { testCaseId } = useParams();

  // Grab active project ID for execution saving
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const projectId = selectedProject?.project_id;
  const canExecuteTests =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");
  const canEditTestCases = canExecuteTests;
  const canCreateBugs = canExecuteTests;

  // --- DYNAMIC THEME VARIABLES ---
  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const inputBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";
  const headerBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";

  // Text Colors
  const textMain = darkMode ? "text-gray-100" : "text-gray-900";
  const textHeading = darkMode ? "text-gray-200" : "text-gray-800";
  const textSub = darkMode ? "text-gray-400" : "text-gray-600";
  const textMuted = darkMode ? "text-gray-500" : "text-gray-400";
  const accentText = darkMode ? "text-indigo-400" : "text-indigo-600";

  // Interaction States
  const btnGhost = darkMode
    ? "text-gray-300 hover:text-white hover:bg-gray-800"
    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";
  const activeToggleBg = darkMode
    ? "bg-gray-800 text-white border-gray-700"
    : "bg-gray-200 text-gray-900 border-gray-300";
  const placeholderColor = darkMode
    ? "placeholder-gray-600"
    : "placeholder-gray-400";
  const optionBg = darkMode ? "bg-[#161922]" : "bg-white";

  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for Manual Execution
  const [isManualExecution, setIsManualExecution] = useState(false);
  const [actualResult, setActualResult] = useState("");
  const [status, setStatus] = useState("Active(Not Executed)");
  const [isSaving, setIsSaving] = useState(false);

  // States for AI Autonomous Execution
  const [isExecutingAI, setIsExecutingAI] = useState(false);

  // States for showing the AI Artifact Results
  const [executionResult, setExecutionResult] = useState(null);
  const [activeTab, setActiveTab] = useState("terminal");

  // State to track automated recent executions for this test case
  const [recentExecutions, setRecentExecutions] = useState([]);

  // States for Failure Intelligence Popup
  const [showFailurePopup, setShowFailurePopup] = useState(false);
  const [failedExecutionData, setFailedExecutionData] = useState(null);

  useEffect(() => {
    const fetchTestCase = async () => {
      try {
        const response = await fetch(
          `https://api.testoraglobal.in/api/testcases/${testCaseId}`,
        );
        if (!response.ok) throw new Error("Test case not found");
        const data = await response.json();

        setTestCase(data);
        setStatus(
          data.status && data.status !== "Active"
            ? data.status
            : "Active(Not Executed)",
        );
        setActualResult(data.actual_result || "");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (testCaseId) fetchTestCase();
  }, [testCaseId]);

  // Fetch Execution History for this specific Test Case
  useEffect(() => {
    const fetchHistory = async () => {
      if (!projectId || !testCase?.title) return;
      try {
        const { data } = await axios.get(
          `https://api.testoraglobal.in/api/history/${projectId}`
        );

        if (Array.isArray(data)) {
          // Filter out runs that match the title AND are NOT manual status updates
          const filteredData = data.filter((run) => 
            run.title === testCase.title && 
            run.duration && 
            String(run.duration).toLowerCase() !== "manual"
          );
          
          const formattedHistory = filteredData.map((run) => ({
            id: run._id,
            title: run.title,
            status: run.status,
            time: run.time || "Recently",
            duration: run.duration,
            log: run.log || run.console_output || "",
            headless: run.headless !== undefined ? run.headless : true,
            screenshots: run.screenshots
              ? run.screenshots.map((s) => `https://api.testoraglobal.in${s}`)
              : run.screenshot
                ? [`https://api.testoraglobal.in${run.screenshot}`]
                : [],
          }));
          setRecentExecutions(formattedHistory);
        }
      } catch (error) {
        console.error("Failed to fetch execution history", error);
      }
    };

    fetchHistory();
  }, [projectId, testCase]);

  // 👉 CHANGED: Added executionScreenshots parameter to capture images
  const handleUpdateExecution = async (
    newStatus = status,
    newActualResult = actualResult,
    executionScreenshots = []
  ) => {
    setIsSaving(true);
    try {
      const response = await axios.put(
        `https://api.testoraglobal.in/api/testcases/${testCase.internal_id || testCase.test_id}`,
        {
          status: newStatus,
          actual_result: newActualResult,
        },
      );

      if (response.status === 200) {
        toast.success("Execution details saved successfully!");
        setTestCase((prev) => ({
          ...prev,
          status: newStatus,
          actual_result: newActualResult,
        }));
        setStatus(newStatus);
        setActualResult(newActualResult);
        setIsManualExecution(false);

        // Trigger Failure Intelligence Popup if the test failed
        if (newStatus === "Fail") {
          setFailedExecutionData({
            title: `[Failed Test] ${testCase.title}`,
            description: `Automated/Manual execution failed for Test Case: ${testCase.test_id || "TC"}. Please review the actual vs expected results.`,
            expected_result: testCase.expected_result || "",
            actual_result: newActualResult || "",
            module: testCase.module || "",
            adjecent_testcaseid: testCase.internal_id || testCase.test_id || testCaseId || "",
            steps: testCase.test_steps || [],
            // 👉 ADDED: Pass the last 2 screenshots to the prefill data
            screenshots: executionScreenshots.slice(-2) 
          });
          setShowFailurePopup(true);
        }
      } else {
        toast.error("Failed to save execution details.");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "Server error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- UPDATED: AI Autonomous Execution Handler ---
  const handleAutonomousRun = async () => {

    if (!testCase?.base_url?.trim())
      alert("Please add a Base URL to this test case before running it.");
    if (!projectId) return toast.error("No active project found.");

    setIsExecutingAI(true);

    try {
      const payload = {
        test_case: {
          "test-case-title": testCase.title,
          test_steps: testCase.test_steps || [],
          expected_result:
            testCase.expected_result || "Execute steps and verify success.",
          base_url: testCase.base_url,
        },
        project_id: projectId,
        headless: true 
      };

      const { data } = await axios.post(
        "https://api.testoraglobal.in/api/run-autonomous-test",
        payload,
      );

      if (data.success) {
        let formattedActualResult = "";
        const fullLogs = data.console_output || "No logs generated.";
        const cleanErrorMessage = data.message || "Execution encountered an error. Check console logs for details.";

        if (data.status === "Pass") {
          formattedActualResult = `✅ Successfully verified: ${testCase.expected_result}`;
        } else {
          formattedActualResult = `❌ Failed to verify: ${testCase.expected_result}\n\nError Summary: ${cleanErrorMessage}`;
        }

        // 👉 CHANGED: Format screenshots array and pass it to handleUpdateExecution
        const screenshotsArray = data.screenshots
            ? data.screenshots.map((s) => `https://api.testoraglobal.in${s}`)
            : [];

        await handleUpdateExecution(data.status, formattedActualResult, screenshotsArray);
        
        setExecutionResult({
          status: data.status,
          log: fullLogs,
          duration: data.duration,
          screenshots: screenshotsArray,
        });

      } else {
        toast.error(`Execution failed: ${data.message}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to communicate with AI Execution Engine."
      );
    } finally {
      setIsExecutingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-indigo-500">
        <Loader2 size={40} className="animate-spin mb-4" />
        <p className={`${textMuted} font-medium`}>
          Loading test case details...
        </p>
      </div>
    );
  }

  if (error || !testCase) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <p className="text-red-500 font-bold mb-2">Error: {error}</p>
        <button
          onClick={() => navigate("/test-cases")}
          className={`${accentText} hover:underline font-medium`}
        >
          Return to Test Cases
        </button>
      </div>
    );
  }

  const preconditionsList = testCase.preconditions
    ? testCase.preconditions.split("\n").filter((p) => p.trim() !== "")
    : ["No specific preconditions required."];

  return (
    <div
      className={`px-8 pb-12 max-w-7xl mx-auto animate-in slide-in-from-right-4 duration-300 ${textMain}`}
    >
      {/* HEADER AREA */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/test-cases")}
          className={`p-2 rounded-full transition-colors ${btnGhost}`}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <span className={`${accentText} font-bold text-xs tracking-widest`}>
              {testCase.test_id || "TC-000"}
            </span>

            <span
              className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${
                testCase.type?.toLowerCase().includes("negative")
                  ? "bg-red-500/10 text-red-600 dark:text-red-500"
                  : testCase.type?.toLowerCase().includes("positive")
                    ? "bg-green-500/10 text-green-600 dark:text-green-500"
                    : "bg-orange-500/10 text-orange-600 dark:text-orange-500"
              }`}
            >
              {testCase.type || "Edge Case"}
            </span>

            {canExecuteTests ? (
              <select
                value={status}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  setStatus(newStatus);
                  handleUpdateExecution(newStatus, actualResult);
                }}
                className={`text-[10px] font-black px-2 py-0.5 rounded uppercase cursor-pointer outline-none appearance-none transition-colors ${
                  status === "Pass"
                    ? "bg-green-500/10 text-green-600 dark:text-green-500 border border-green-500/20"
                    : status === "Fail"
                      ? "bg-red-500/10 text-red-600 dark:text-red-500 border border-red-500/20"
                      : `bg-gray-500/10 ${textMuted} border border-gray-500/20`
                }`}
              >
                <option
                  value="Active(Not Executed)"
                  className={`${optionBg} ${textMuted}`}
                >
                  Active
                </option>
                <option
                  value="Pass"
                  className={`${optionBg} text-green-600 dark:text-green-500`}
                >
                  Pass
                </option>
                <option
                  value="Fail"
                  className={`${optionBg} text-red-600 dark:text-red-500`}
                >
                  Fail
                </option>
              </select>
            ) : (
              <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${textMuted}`}>
                {status}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold tracking-tight">
            {testCase.title}
          </h2>
        </div>

        <div className="ml-auto flex gap-3">
          {canExecuteTests && (
            <button
              onClick={() => setIsManualExecution(!isManualExecution)}
              className={`border ${borderColor} px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold transition-all ${isManualExecution ? activeToggleBg : btnGhost}`}
            >
              <ClipboardEdit size={16} />{" "}
              {isManualExecution ? "Close Manual Execution" : "Executed Manually"}
            </button>
          )}

          {/* AI AUTONOMOUS RUN BUTTON */}
          {canExecuteTests && (
            <button
              onClick={handleAutonomousRun}
              disabled={isExecutingAI}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold transition-all shadow-lg hover:shadow-indigo-500/25"
            >
              {isExecutingAI ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Play size={16} fill="currentColor" />
              )}
              {isExecutingAI ? "Executing AI..." : "Run Test"}
            </button>
          )}

          {canEditTestCases && (
            <button
              onClick={() =>
                navigate(
                  `/test-cases/edit/${testCase.internal_id || testCase.test_id}`,
                )
              }
              className={`border ${borderColor} px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold transition-all ${btnGhost}`}
            >
              <Edit3 size={16} /> Edit
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* DESCRIPTION CARD */}
        <div
          className={`p-6 rounded-2xl border ${cardBg} ${borderColor} shadow-sm transition-colors`}
        >
          <h3 className={`font-bold text-sm mb-4 ${textHeading}`}>
            Description
          </h3>
          <p className={`${textSub} text-sm leading-relaxed`}>
            {testCase.description || "No description provided."}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-500/20 flex gap-6">
            <span
              className={`text-xs ${textMuted} uppercase font-bold tracking-wider`}
            >
              Module:{" "}
              <span className={`${accentText} normal-case ml-1`}>
                {testCase.module || "General"}
              </span>
            </span>
            <span
              className={`text-xs ${textMuted} uppercase font-bold tracking-wider`}
            >
              Feature:{" "}
              <span className={`${accentText} normal-case ml-1`}>
                {testCase.feature_submodule || "N/A"}
              </span>
            </span>
            <span
              className={`text-xs ${textMuted} uppercase font-bold tracking-wider`}
            >
              Category:{" "}
              <span className={`${accentText} normal-case ml-1`}>
                {testCase.category || "N/A"}
              </span>
            </span>
          </div>
        </div>

        {/* PRECONDITIONS */}
        <div
          className={`p-6 rounded-2xl border ${cardBg} ${borderColor} shadow-sm transition-colors`}
        >
          <h3 className={`font-bold text-sm mb-4 ${textHeading}`}>
            Preconditions
          </h3>
          <ul className="space-y-3">
            {preconditionsList.map((item, i) => (
              <li
                key={i}
                className={`flex items-center gap-3 text-sm ${textSub}`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />{" "}
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* TEST STEPS */}
        <div
          className={`p-6 rounded-2xl border ${cardBg} ${borderColor} shadow-sm transition-colors`}
        >
          <h3 className={`font-bold text-sm mb-6 ${textHeading}`}>
            Execution Steps
          </h3>
          <div className="space-y-0">
            {Array.isArray(testCase.test_steps) &&
            testCase.test_steps.length > 0 ? (
              testCase.test_steps.map((step, index) => (
                <StepItem
                  key={index}
                  number={index + 1}
                  action={step}
                  expected={""}
                  last={index === testCase.test_steps.length - 1}
                  darkMode={darkMode}
                />
              ))
            ) : (
              <p className={`${textMuted} text-sm italic`}>
                No execution steps defined.
              </p>
            )}
          </div>
        </div>

        {/* EXPECTED RESULT */}
        <div
          className={`p-6 rounded-2xl border border-green-500/20 ${darkMode ? "bg-green-500/5" : "bg-green-50"} shadow-sm transition-colors`}
        >
          <h3 className="font-bold text-sm mb-3 text-green-600 dark:text-green-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Expected Result
          </h3>
          <p
            className={`text-sm ${textSub} leading-relaxed pl-4 border-l-2 border-green-500/30`}
          >
            {testCase.expected_result || "No expected result defined."}
          </p>
        </div>

        {/* --- READ-ONLY ACTUAL RESULT --- */}
        {testCase.actual_result && !isManualExecution && (
          <div
            className={`p-6 rounded-2xl border shadow-sm transition-colors animate-in fade-in duration-500 ${
              testCase.status === "Fail"
                ? darkMode
                  ? "border-red-500/20 bg-red-500/5"
                  : "border-red-500/30 bg-red-50"
                : testCase.status === "Pass"
                  ? darkMode
                    ? "border-green-500/20 bg-green-500/5"
                    : "border-green-500/30 bg-green-50"
                  : darkMode
                    ? "border-blue-500/20 bg-blue-500/5"
                    : "border-blue-500/30 bg-blue-50"
            }`}
          >
            <h3
              className={`font-bold text-sm mb-3 flex items-center gap-2 ${
                testCase.status === "Fail"
                  ? "text-red-600 dark:text-red-500"
                  : testCase.status === "Pass"
                    ? "text-green-600 dark:text-green-500"
                    : "text-blue-600 dark:text-blue-500"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  testCase.status === "Fail"
                    ? "bg-red-500"
                    : testCase.status === "Pass"
                      ? "bg-green-500"
                      : "bg-blue-500"
                }`}
              />
              Actual Result
            </h3>
            <p
              className={`text-sm ${textSub} leading-relaxed pl-4 border-l-2 whitespace-pre-wrap font-mono ${
                testCase.status === "Fail"
                  ? "border-red-500/30"
                  : testCase.status === "Pass"
                    ? "border-green-500/30"
                    : "border-blue-500/30"
              }`}
            >
              {testCase.actual_result}
            </p>
          </div>
        )}

        {/* MANUAL TEXT EDITOR */}
        {canExecuteTests && isManualExecution && (
          <div
            className={`p-6 rounded-2xl border shadow-lg transition-all animate-in fade-in zoom-in-95 duration-300 ${cardBg} ${borderColor}`}
          >
            <h3
              className={`font-bold text-sm mb-4 flex items-center gap-2 ${accentText}`}
            >
              <ClipboardEdit size={18} /> Actual Result (Manual Execution)
            </h3>
            <textarea
              value={actualResult}
              onChange={(e) => setActualResult(e.target.value)}
              placeholder="What actually happened when you executed this test case?"
              className={`w-full p-4 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-indigo-500 transition-all text-sm resize-none ${textMain} ${placeholderColor}`}
              rows="4"
            />
            <div className="flex justify-end items-center mt-4">
              <button
                onClick={() => handleUpdateExecution()}
                disabled={isSaving || !actualResult.trim()}
                className={`group inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  isSaving || !actualResult.trim()
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500"
                    : "bg-indigo-600 text-white hover:bg-indigo-500"
                }`}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Save size={17} /> Submit Result
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* RECENT AUTOMATED EXECUTIONS */}
        <div className="mt-8">
          <h3 className={`font-bold text-sm mb-4 ${textHeading}`}>Recent Automated Executions</h3>
          <div className={`rounded-2xl border overflow-hidden shadow-sm ${cardBg} ${borderColor}`}>
            <div className="divide-y divide-gray-800/50 dark:divide-gray-800 max-h-[400px] overflow-y-auto custom-scrollbar">
              {!Array.isArray(recentExecutions) || recentExecutions.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No automated executions found for this test case yet.
                </div>
              ) : (
                recentExecutions.map((run) => (
                  <div
                    key={run.id}
                    onClick={() => {
                      setActiveTab("terminal");
                      setExecutionResult({
                        status: run.status,
                        log: run.log,
                        duration: run.duration,
                        screenshots: run.screenshots,
                      });
                    }}
                    className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                      darkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {run.status === "Pass" ? (
                        <CheckCircle size={20} className="text-green-500" />
                      ) : (
                        <XCircle size={20} className="text-red-500" />
                      )}
                      <div>
                        <p className={`text-sm font-semibold ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                          {run.title}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span className="flex items-center gap-1">
                            <Clock size={12} /> {run.time}
                          </span>
                          <span>•</span>
                          <span>{run.duration}</span>
                          <span>•</span>
                          <span>{run.headless ? "Background Run" : "Live Execution"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-gray-400 hover:text-indigo-500 transition-colors">
                      <Terminal size={18} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>

      {/* --------------------------------------------------- */}
      {/* OVERLAY: EXECUTING FULL SCREEN LOADER */}
      {/* --------------------------------------------------- */}
      {isExecutingAI && (
        <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in">
          <div className="relative flex items-center justify-center mb-6">
            <div className="absolute inset-0 bg-indigo-500 blur-[50px] opacity-30 rounded-full animate-pulse" />
            <div className="w-20 h-20 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
            <Play
              size={24}
              className="absolute text-indigo-400"
              fill="currentColor"
            />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Executing test steps...
          </h2>
          <p className="text-indigo-300 text-sm font-mono bg-indigo-500/10 px-4 py-2 rounded-lg border border-indigo-500/20">
            Target: {testCase.base_url}
          </p>
        </div>
      )}

      {/* --------------------------------------------------- */}
      {/* MODAL 2: AI EXECUTION ARTIFACT RESULT */}
      {/* --------------------------------------------------- */}
      {executionResult && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div
            className={`w-full max-w-4xl rounded-2xl shadow-2xl border flex flex-col overflow-hidden ${darkMode ? "bg-[#161922] border-gray-800" : "bg-white border-gray-200"}`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center justify-between ${headerBg} ${borderColor}`}
            >
              <div className="flex items-center gap-3">
                {executionResult.status === "Pass" ? (
                  <CheckCircle size={24} className="text-green-500" />
                ) : (
                  <XCircle size={24} className="text-red-500" />
                )}
                <div>
                  <h3 className="font-bold text-lg leading-none">
                    {executionResult.status === "Pass"
                      ? "Test Passed"
                      : "Test Failed"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Database automatically updated • {executionResult.duration}{" "}
                    run time
                  </p>
                </div>
              </div>
              <button
                onClick={() => setExecutionResult(null)}
                className="p-2 hover:bg-gray-500/20 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div
              className={`px-6 py-2 border-b flex gap-4 ${borderColor} bg-gray-900/10`}
            >
              <button
                onClick={() => setActiveTab("terminal")}
                className={`flex items-center gap-2 py-2 text-sm font-bold border-b-2 transition-colors ${activeTab === "terminal" ? "border-indigo-500 text-indigo-500" : "border-transparent text-gray-500 hover:text-gray-300"}`}
              >
                <Terminal size={16} /> Console Logs
              </button>
              <button
                onClick={() => setActiveTab("screenshot")}
                className={`flex items-center gap-2 py-2 text-sm font-bold border-b-2 transition-colors ${activeTab === "screenshot" ? "border-indigo-500 text-indigo-500" : "border-transparent text-gray-500 hover:text-gray-300"}`}
              >
                <ImageIcon size={16} /> Visual Artifacts
              </button>
            </div>

            <div className="h-[400px] bg-black p-0 overflow-hidden relative">
              {activeTab === "terminal" ? (
                <div className="p-6 h-full overflow-y-auto font-mono text-sm">
                  <pre
                    className={`${executionResult.status === "Pass" ? "text-green-400" : "text-red-400"} whitespace-pre-wrap leading-relaxed`}
                  >
                    {executionResult.log}
                  </pre>
                </div>
              ) : (
                <div className="h-full overflow-y-auto bg-gray-900 p-6">
                  {executionResult.screenshots &&
                  executionResult.screenshots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-6">
                      {executionResult.screenshots.map((imgUrl, idx) => (
                        <div key={idx} className="flex flex-col gap-2">
                          <span className="text-xs font-bold text-gray-400 bg-gray-800 px-3 py-1 rounded-full w-max">
                            Step {idx + 1}
                          </span>
                          <img
                            src={imgUrl}
                            alt={`Step ${idx + 1} Artifact`}
                            className="w-full h-auto object-contain rounded border border-gray-700 shadow-lg bg-black"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center p-4">
                      <p className="text-gray-500 flex items-center gap-2">
                        <ImageIcon size={20} /> No screenshot artifacts
                        available
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --------------------------------------------------- */}
      {/* MODAL 3: FAILURE INTELLIGENCE POPUP */}
      {/* --------------------------------------------------- */}
      {showFailurePopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl border p-6 ${cardBg} ${borderColor} text-center`}>
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
              <AlertTriangle size={32} className="text-red-500" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${textHeading}`}>
              Test Case Failed
            </h3>
            <p className={`text-sm ${textSub} mb-6 leading-relaxed`}>
              This test case execution resulted in a failure. Would you like to seamlessly report this as a bug with pre-filled details?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowFailurePopup(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all border ${borderColor} ${btnGhost}`}
              >
                Reject
              </button>
              {canCreateBugs && (
                <button
                  onClick={() => {
                    setShowFailurePopup(false);
                    navigate("/bug-optimizer/createbug", { state: { prefillData: failedExecutionData } });
                  }}
                  className="bg-red-600 hover:bg-red-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-red-500/20 transition-all flex items-center gap-2"
                >
                  View Details & Report Bug
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}